import { ActionButton, FeedItem } from '@knocklabs/client';
import { formatTimestamp } from '@knocklabs/react';
import { MessageDescriptor } from 'react-intl';
import { getNotificationItemContent } from '../../shared/get-notification-item-content';
import { getNotificationActionUrl } from '../../shared/notification-action-url';
import { NotificationSeverity, getNotificationSeverity } from '../../shared/notification-severity';
import { getNotificationWorkflow, notificationWorkflowCategoryMessages } from '../../workflows';
import { getNotificationItemAvatar } from './get-notification-item-avatar';
import { getNotificationItemButtons } from './get-notification-item-buttons';

export type NotificationFeedListItemModel = {
  id: string;
  /** Product-area label from the workflow category. */
  eyebrow: string | null;
  title: string;
  bodyHtml: string | null;
  timeLabel: string;
  unread: boolean;
  avatarName: string | null;
  avatarSrc: string | null;
  buttons: ActionButton[];
  /** True when the row is clickable to navigate (has a primary action URL). */
  hasAction: boolean;
  severity: NotificationSeverity;
};

/**
 * Maps a Knock FeedItem into the presentational list-item model.
 * Title/body come from Knock markdown (first paragraph / remainder), not `item.data`.
 * Eyebrow is the workflow category label when the workflow is registered.
 */
export function mapNotificationFeedItem(
  item: FeedItem,
  fallbackTitle: string,
  formatMessage: (descriptor: MessageDescriptor) => string,
): NotificationFeedListItemModel {
  const avatar = getNotificationItemAvatar(item);
  const { title, bodyHtml } = getNotificationItemContent(item, fallbackTitle);
  const workflow = getNotificationWorkflow(item.source?.key);

  return {
    id: item.id,
    eyebrow: workflow
      ? formatMessage(notificationWorkflowCategoryMessages[workflow.category])
      : null,
    title,
    bodyHtml,
    timeLabel: formatTimestamp(item.inserted_at),
    unread: item.read_at == null,
    avatarName: avatar?.name ?? null,
    avatarSrc: avatar?.src ?? null,
    buttons: getNotificationItemButtons(item, formatMessage),
    hasAction: getNotificationActionUrl(item) != null,
    severity: getNotificationSeverity(item.source?.key),
  };
}
